import { useMemo } from "react";
import { Avatar, Dropdown, Modal, Tooltip } from "antd";
import {
  DownOutlined,
  LogoutOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { QuestionCircleOutlined } from "@ant-design/icons";

import { MsgProvider, MsgPop } from "@weee-fe/msg-central";
import useUsers from "@hooks/useUsers";
import ThemeToken from "@/configs/theme";
import globalState from "@/models/global";
import { DATE_FORMAT_US } from "@constants/common";

import styles from "./header.module.less";

const avatarDropDownItems: MenuProps["items"] = [
  {
    key: "accountSettings",
    label: "Account Settings",
    icon: <SettingOutlined />,
  },
  {
    key: "logout",
    label: "Log out",
    icon: <LogoutOutlined />,
  },
];

export default () => {
  const nav = useNavigate();
  const { getUserInfoFromLocal, clearLocalUserInfo } = useUsers();
  const { vendor_list = [], current_vendor, token } = getUserInfoFromLocal();

  const [currentGlobalState, setGlobalState] = useRecoilState(globalState);

  const vendorDropDownItems: MenuProps["items"] = useMemo(() => {
    return vendor_list?.map((item) => ({
      key: item.id,
      label: item.title,
    }));
  }, [vendor_list]);

  const logout = (url: string = "/login") => {
    clearLocalUserInfo();
    nav(url);
  };

  const handleAvatarDropDownItemClick: MenuProps["onClick"] = ({ key }) => {
    switch (key) {
      case "logout":
        logout();
        return;
      case "accountSettings":
        nav("/user/settings/info");
        return;
    }
  };

  const handleVendorChange: MenuProps["onClick"] = ({ key }) => {
    if (key === current_vendor?.id) return;

    const newVendor = vendor_list?.find((vendor) => vendor.id === key);
    Modal.confirm({
      title: "Action",
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to change from [${current_vendor?.title}] to [${newVendor?.title}]? If so, You need to log out and log back in`,
      onOk() {
        logout(`/login?vendor_id=${newVendor?.id}&type=${newVendor?.type}`);
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  const handleChange = (messageVisible: boolean) => {
    setGlobalState({
      ...currentGlobalState,
      messageVisible,
    });
  };

  return (
    <div className={styles.header}>
      {vendor_list?.length > 1 && (
        <Dropdown
          menu={{
            items: vendorDropDownItems,
            selectedKeys: [current_vendor?.id],
            onClick: handleVendorChange,
          }}
        >
          <span className={styles.currentVendor}>
            {current_vendor?.title}
            <DownOutlined className={styles.icon} />
          </span>
        </Dropdown>
      )}

      <MsgProvider env={process.env.REACT_APP_ENV as any} token={token}>
        <MsgPop
          trigger="hover"
          timeFormat={`${DATE_FORMAT_US} HH:mm`}
          onlyShowUnreadDefaultChecked
          colorPrimary={ThemeToken.colorPrimary}
          open={currentGlobalState?.messageVisible}
          onOpenChange={handleChange}
        />
      </MsgProvider>

      <Tooltip title="click to go to help center">
        <Link to="/help-in/center">
          <QuestionCircleOutlined className={styles.iconWrapper} />
        </Link>
      </Tooltip>

      <Dropdown
        menu={{
          items: avatarDropDownItems,
          onClick: handleAvatarDropDownItemClick,
        }}
      >
        <Avatar
          className={styles.avatar}
          shape="square"
          size={40}
          src="https://www.sayweee.com/css/img/avatar_unknown.png"
        />
      </Dropdown>
    </div>
  );
};
